import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, balanceLabel, phoneFmt } from '../api';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, useToast } from '../ui';

type ClientRow = {
  id: string;
  code: string;
  name: string;
  phone: string;
  type: string;
  cachedBalanceKop: string;
  cachedBottles: number;
  addresses: Array<{ street: string; house: string; flat: string | null }>;
};

export function Clients() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [debtOnly, setDebtOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [showNew, setShowNew] = useState(!!params.get('new'));
  const toast = useToast();

  // Поиск с задержкой: оператор печатает быстро, запрос на каждую букву
  // только мешает
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const url = `/clients?limit=100${query ? `&search=${encodeURIComponent(query)}` : ''}${debtOnly ? '&debtOnly=true' : ''}`;
  const { data, error, loading, reload } = useApi<{ items: ClientRow[]; total: number }>(url);

  return (
    <>
      <div className="spread">
        <h1>Клиенты{data ? ` — ${data.total}` : ''}</h1>
        <button className="primary" onClick={() => setShowNew(true)}>
          + Новый клиент
        </button>
      </div>

      <div className="card">
        <div className="row">
          <input
            style={{ flex: '1 1 320px' }}
            placeholder="Имя, телефон или код клиента"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
            <input
              type="checkbox"
              style={{ width: 16 }}
              checked={debtOnly}
              onChange={(e) => setDebtOnly(e.target.checked)}
            />
            только должники
          </label>
        </div>
      </div>

      {showNew ? (
        <NewClientForm
          phone={params.get('new') ?? ''}
          onClose={() => {
            setShowNew(false);
            params.delete('new');
            setParams(params);
          }}
          onCreated={() => {
            toast.show('Клиент заведён');
            void reload();
          }}
        />
      ) : null}

      <div className="card">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorBox error={error} />
        ) : !data?.items.length ? (
          <Empty>Никого не нашлось. Проверьте написание или заведите нового клиента.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Код</th>
                <th>Клиент</th>
                <th>Телефон</th>
                <th>Адрес</th>
                <th className="num">Баланс</th>
                <th className="num">Тара</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => {
                const b = balanceLabel(c.cachedBalanceKop);
                const a = c.addresses[0];
                return (
                  <tr key={c.id}>
                    <td className="mono muted">{c.code}</td>
                    <td>
                      <Link to={`/clients/${c.id}`}>{c.name}</Link>
                      {c.type === 'COMPANY' ? <span className="badge" style={{ marginLeft: 6 }}>юр.</span> : null}
                    </td>
                    <td className="mono">{phoneFmt(c.phone)}</td>
                    <td className="muted">{a ? `${a.street}, ${a.house}${a.flat ? `, ${a.flat}` : ''}` : '—'}</td>
                    <td className={`num ${b.kind === 'debt' ? 'debt' : b.kind === 'prepaid' ? 'prepaid' : ''}`}>
                      {b.text}
                    </td>
                    <td className="num">{c.cachedBottles}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {toast.node}
    </>
  );
}

function NewClientForm({
  phone,
  onClose,
  onCreated,
}: {
  phone: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    name: '',
    phone,
    type: 'PERSON',
    street: '',
    house: '',
    flat: '',
    comment: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/clients', {
        body: {
          name: form.name,
          phone: form.phone,
          type: form.type,
          comment: form.comment || undefined,
          source: 'operator',
          address: form.street
            ? { street: form.street, house: form.house, flat: form.flat || undefined }
            : undefined,
        },
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="spread">
        <h2>Новый клиент</h2>
        <button type="button" className="ghost" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="grid c3">
        <div className="field">
          <label>Имя или название</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div className="field">
          <label>Телефон</label>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
        </div>
        <div className="field">
          <label>Тип</label>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="PERSON">Физлицо</option>
            <option value="COMPANY">Юрлицо</option>
          </select>
        </div>
        <div className="field">
          <label>Улица</label>
          <input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} />
        </div>
        <div className="field">
          <label>Дом</label>
          <input value={form.house} onChange={(e) => setForm({ ...form, house: e.target.value })} />
        </div>
        <div className="field">
          <label>Квартира / офис</label>
          <input value={form.flat} onChange={(e) => setForm({ ...form, flat: e.target.value })} />
        </div>
      </div>
      <div className="field">
        <label>Комментарий (что важно знать водителю)</label>
        <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
      </div>
      <button className="primary" disabled={busy}>
        Сохранить
      </button>
      <ErrorBox error={error} />
    </form>
  );
}
